import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMap = {
  G: { label: "On Track", color: "bg-green-500", text: "text-green-700 dark:text-green-400", bg: "bg-green-50 dark:bg-green-900/30" },
  Y: { label: "At Risk", color: "bg-yellow-500", text: "text-yellow-700 dark:text-yellow-400", bg: "bg-yellow-50 dark:bg-yellow-900/30" },
  R: { label: "Off Track", color: "bg-red-500", text: "text-red-700 dark:text-red-400", bg: "bg-red-50 dark:bg-red-900/30" },
  C: { label: "Complete", color: "bg-blue-500", text: "text-blue-700 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-900/30" },
  N: { label: "Not Started", color: "bg-gray-400", text: "text-gray-700 dark:text-gray-400", bg: "bg-gray-50 dark:bg-gray-900/30" },
};

const msStatusMap = { Completed: "C", "On Track": "G", "In Progress": "G", "At Risk": "Y", Delayed: "R", "Off Track": "R", "Not Started": "N", "On Time": "G" };

const fmtDate = (d) => {
  if (!d) return "";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
  return d;
};

export default function SharedQuadChart() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = window.location.pathname.split("/share/quadchart/")[1];
    if (!token) { setError("Invalid link"); return; }
    axios.get(`${API}/quadchart/share/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setError("This share link is invalid or the Quad Chart has no data."));
  }, []);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0B0F1A]">
      <Card className="max-w-md w-full mx-4"><CardContent className="p-8 text-center">
        <p className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Link Not Found</p>
        <p className="text-sm text-slate-500 dark:text-slate-400">{error}</p>
      </CardContent></Card>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0B0F1A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00D4C8]" />
    </div>
  );

  const st = statusMap[data.overall_status] || statusMap.N;
  const milestones = data.milestones_data || [];
  const reqSummary = data.requirements_summary || { total: 0, completed: 0 };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B0F1A] py-8 px-4" data-testid="shared-quadchart">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-2">Project Status Report</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">{data.project_name || "Quad Chart"}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 dark:text-slate-400">
              {data.owner && <span>Owner: <span className="font-medium text-slate-700 dark:text-slate-200">{data.owner}</span></span>}
              {data.report_date && <span>Date: <span className="font-medium text-slate-700 dark:text-slate-200">{fmtDate(data.report_date)}</span></span>}
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${st.bg} border`}>
            <div className={`w-3 h-3 rounded-full ${st.color}`} />
            <span className={`text-sm font-bold ${st.text}`}>{st.label}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals */}
        <Card className="bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#00D4C8] mb-4">Project Description / Goals</h2>
            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{data.goals || "No goals specified."}</p>
          </CardContent>
        </Card>

        {/* Milestones */}
        <Card className="bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#00D4C8] mb-4">Milestones</h2>
            {milestones.length === 0 ? (
              <p className="text-sm text-slate-400">No milestones selected.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-200 dark:border-slate-600">
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600 dark:text-slate-300">Milestone</th>
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600 dark:text-slate-300">End Date</th>
                    <th className="text-center py-2 font-semibold text-slate-600 dark:text-slate-300">Status</th>
                  </tr></thead>
                  <tbody>
                    {milestones.map((ms) => {
                      const code = msStatusMap[ms.status] || "N";
                      const s = statusMap[code] || statusMap.N;
                      return (
                        <tr key={ms.id} className="border-b border-slate-100 dark:border-slate-700 last:border-0">
                          <td className="py-2 pr-4 text-slate-800 dark:text-slate-200">{ms.title}</td>
                          <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">{fmtDate(ms.target_end) || "—"}</td>
                          <td className="py-2 text-center">
                            <Badge className={`${s.bg} ${s.text} border-0 text-xs font-bold`}>{code}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Highlights */}
        <Card className="bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#00D4C8] mb-4">Highlights / Major Accomplishments</h2>
            {(data.highlights || []).length === 0 ? (
              <p className="text-sm text-slate-400">No highlights added.</p>
            ) : (
              <ul className="space-y-2">
                {data.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="text-green-500 mt-0.5 font-bold">+</span> {h}
                  </li>
                ))}
              </ul>
            )}
            {reqSummary.completed > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Requirements: <span className="font-bold text-green-600 dark:text-green-400">{reqSummary.completed}</span> / {reqSummary.total} completed
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming */}
        <Card className="bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#00D4C8] mb-4">Upcoming Activities</h2>
            {(data.upcoming || []).length === 0 ? (
              <p className="text-sm text-slate-400">No upcoming activities.</p>
            ) : (
              <ul className="space-y-2">
                {data.upcoming.map((u, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="text-blue-500 mt-0.5 font-bold">&#8594;</span> {u}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Issues / Risks — Full Width */}
      {(data.issues || []).length > 0 && (
        <div className="max-w-7xl mx-auto mt-6">
          <Card className="bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#00D4C8] mb-4">Issues / Risks</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-200 dark:border-slate-600">
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600 dark:text-slate-300">Issue / Risk</th>
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600 dark:text-slate-300">Owner</th>
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600 dark:text-slate-300">Mitigation</th>
                    <th className="text-center py-2 pr-4 font-semibold text-slate-600 dark:text-slate-300">Impact</th>
                    <th className="text-left py-2 font-semibold text-slate-600 dark:text-slate-300">Due Date</th>
                  </tr></thead>
                  <tbody>
                    {data.issues.map((issue, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-700 last:border-0">
                        <td className="py-2 pr-4 text-slate-800 dark:text-slate-200">{issue.issue}</td>
                        <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">{issue.owner}</td>
                        <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">{issue.mitigation}</td>
                        <td className="py-2 pr-4 text-center">
                          <Badge className={`text-xs font-bold border-0 ${issue.impact === "H" ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400" : issue.impact === "M" ? "bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400" : "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400"}`}>{issue.impact}</Badge>
                        </td>
                        <td className="py-2 text-slate-500 dark:text-slate-400">{fmtDate(issue.due_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <div className="max-w-7xl mx-auto mt-8 flex items-center justify-center gap-6 text-xs text-slate-400">
        {Object.entries(statusMap).map(([code, cfg]) => (
          <div key={code} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${cfg.color}`} />
            <span>{code} — {cfg.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
